import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Play, Pause, CheckCircle, XCircle, Clock, RefreshCw, Plus, Eye, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';

const RUN_TYPES = [
  { value: 'rent_collection', label: 'Rent Collection' },
  { value: 'landlord_payout', label: 'Landlord Payout' },
  { value: 'contractor_payout', label: 'Contractor Payout' },
  { value: 'deposit_lodgement', label: 'Deposit Lodgement' },
  { value: 'deposit_return', label: 'Deposit Return' },
  { value: 'manual_adjustment', label: 'Manual Adjustment' },
];

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  reconciling: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  completed_with_errors: 'bg-orange-100 text-orange-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

export default function FinanceRuns() {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filter, setFilter] = useState({ type: '', status: '' });
  const [formData, setFormData] = useState({
    run_type: 'rent_collection',
    description: '',
    period_start: new Date().toISOString().split('T')[0],
    period_end: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchRuns();
  }, [filter]);

  const fetchRuns = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.type) params.append('run_type', filter.type);
      if (filter.status) params.append('status', filter.status);
      const response = await api.get(`/finance/runs?${params}`);
      setRuns(response.data);
    } catch (error) {
      console.error('Error fetching runs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRun = async (e) => {
    e.preventDefault();
    try {
      await api.post('/finance/runs', formData);
      setDialogOpen(false);
      resetForm();
      fetchRuns();
    } catch (error) {
      console.error('Error creating run:', error);
    }
  };

  const handleStartRun = async (runId) => {
    try {
      await api.post(`/finance/runs/${runId}/start`);
      fetchRuns();
    } catch (error) {
      console.error('Error starting run:', error);
    }
  };

  const handleCompleteRun = async (runId) => {
    try {
      await api.post(`/finance/runs/${runId}/complete`);
      fetchRuns();
    } catch (error) {
      console.error('Error completing run:', error);
    }
  };

  const handleCancelRun = async (runId) => {
    if (window.confirm('Are you sure you want to cancel this run?')) {
      try {
        await api.post(`/finance/runs/${runId}/cancel`);
        fetchRuns();
      } catch (error) {
        console.error('Error cancelling run:', error);
      }
    }
  };

  const viewRunDetails = async (run) => {
    try {
      const response = await api.get(`/finance/runs/${run.id}`);
      setSelectedRun(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching run details:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      run_type: 'rent_collection',
      description: '',
      period_start: new Date().toISOString().split('T')[0],
      period_end: new Date().toISOString().split('T')[0],
      due_date: new Date().toISOString().split('T')[0],
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Finance Runs</h1>
            <p className="text-gray-500">Manage batch processing runs for collections and payouts</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                New Run
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Finance Run</DialogTitle>
                <DialogDescription>
                  Create a new batch processing run for financial operations.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateRun} className="space-y-4">
                <div className="space-y-2">
                  <Label>Run Type</Label>
                  <Select value={formData.run_type} onValueChange={(v) => setFormData({...formData, run_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RUN_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Optional description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Period Start</Label>
                    <Input
                      type="date"
                      value={formData.period_start}
                      onChange={(e) => setFormData({...formData, period_start: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Period End</Label>
                    <Input
                      type="date"
                      value={formData.period_end}
                      onChange={(e) => setFormData({...formData, period_end: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    required
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Create Run</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="w-48">
                <Select value={filter.type || "all_types"} onValueChange={(v) => setFilter({...filter, type: v === "all_types" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_types">All Types</SelectItem>
                    {RUN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Select value={filter.status || "all_statuses"} onValueChange={(v) => setFilter({...filter, status: v === "all_statuses" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_statuses">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => setFilter({ type: '', status: '' })}>
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Runs Table */}
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Run Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map(run => (
                  <TableRow key={run.id}>
                    <TableCell className="font-mono">{run.run_number}</TableCell>
                    <TableCell>
                      {RUN_TYPES.find(t => t.value === run.run_type)?.label || run.run_type}
                    </TableCell>
                    <TableCell>
                      {run.period_start} to {run.period_end}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[run.status] || 'bg-gray-100'}>
                        {run.status.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {run.successful_items}/{run.total_items}
                      {run.failed_items > 0 && (
                        <span className="text-red-500 ml-1">({run.failed_items} failed)</span>
                      )}
                    </TableCell>
                    <TableCell>{formatCurrency(run.total_amount)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => viewRunDetails(run)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {run.status === 'pending' && (
                          <Button size="sm" variant="ghost" onClick={() => handleStartRun(run.id)}>
                            <Play className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                        {(run.status === 'in_progress' || run.status === 'reconciling') && (
                          <Button size="sm" variant="ghost" onClick={() => handleCompleteRun(run.id)}>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                        {!['completed', 'completed_with_errors', 'cancelled'].includes(run.status) && (
                          <Button size="sm" variant="ghost" onClick={() => handleCancelRun(run.id)}>
                            <XCircle className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {runs.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No finance runs found. Create your first run to get started.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Run Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Run Details: {selectedRun?.run_number}</DialogTitle>
            </DialogHeader>
            {selectedRun && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-500">Total Amount</div>
                      <div className="text-2xl font-bold">{formatCurrency(selectedRun.total_amount)}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-500">Successful</div>
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(selectedRun.successful_amount)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-500">Failed</div>
                      <div className="text-2xl font-bold text-red-600">
                        {formatCurrency(selectedRun.failed_amount)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-500">Items</div>
                      <div className="text-2xl font-bold">
                        {selectedRun.successful_items}/{selectedRun.total_items}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Expected</TableHead>
                      <TableHead>Actual</TableHead>
                      <TableHead>Payment Ref</TableHead>
                      <TableHead>Processed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedRun.items || []).map(item => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-xs">{item.id.slice(0, 8)}...</TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[item.status] || 'bg-gray-100'}>
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatCurrency(item.expected_amount)}</TableCell>
                        <TableCell>{item.actual_amount ? formatCurrency(item.actual_amount) : '-'}</TableCell>
                        <TableCell>{item.payment_reference || '-'}</TableCell>
                        <TableCell>{item.processed_at ? new Date(item.processed_at).toLocaleString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
